import React from "react";
import {
  Box,
  Stack,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Skeleton,
} from "@mui/material";
import { ReactComponent as DropdownArrow } from "../../../../assets/images/dropdown-arrow.svg";
import { ReactComponent as Urgent } from "../../../../assets/images/TicketImages/urgent.svg";
import { ReactComponent as High } from "../../../../assets/images/TicketImages/high.svg";
import { ReactComponent as Medium } from "../../../../assets/images/TicketImages/medium.svg";
import { ReactComponent as Low } from "../../../../assets/images/TicketImages/low.svg";
import { IconSource16 } from "../TicketV2Icons";
import {
  inter,
  selectMenuProps,
  sxMetaLabel,
  sxMetaDateValue,
  tv2PropertySelectSx,
  tv2SelectPlaceholderTypographySx,
  tv2SelectValueRowSx,
  tv2SelectValueTypographySx,
} from "./styles";

const STATUS_META = {
  open: { label: "Open", menuLabel: "Submit as Open", color: "#148DFD" },
  solved: { label: "Solved", menuLabel: "Submit as Solved", color: "#52C41A" },
  pending: { label: "Pending", menuLabel: "Submit as Pending", color: "#FA8C16" },
  spam: { label: "Spam", menuLabel: "Submit as Spam", color: "#93A1B0" },
};

function getStatusKey(value) {
  return String(value || "").trim().toLowerCase();
}

const PRIORITY_META = [
  { key: "Urgent", icon: Urgent, gap: 1 },
  { key: "High", icon: High, gap: 1 },
  { key: "Medium", icon: Medium, gap: 1 },
  { key: "Low", icon: Low, gap: 1.875, iconSx: { marginLeft: "5px" } },
];

export function DetailsFormSkeleton() {
  return (
    <Stack gap={2} sx={{ py: 1 }}>
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <Stack key={i} gap={0.75}>
          <Skeleton width={72} height={14} />
          <Skeleton variant="rounded" width={248} height={40} sx={{ borderRadius: "8px" }} />
        </Stack>
      ))}
    </Stack>
  );
}

/** History section rows in Ticket Details sidebar while user-history loads. */
export function HistoryPreviewSkeleton({ rows = 5 }) {
  return (
    <Stack gap={1}>
      {Array.from({ length: rows }, (_, i) => (
        <Stack key={i} direction="row" alignItems="center" gap={1}>
          <Skeleton variant="rounded" width={16} height={16} sx={{ borderRadius: "4px", flexShrink: 0 }} />
          <Skeleton
            variant="rounded"
            height={14}
            sx={{
              flex: 1,
              borderRadius: "4px",
              maxWidth: i % 2 === 0 ? "92%" : "78%",
            }}
          />
        </Stack>
      ))}
      <Skeleton variant="rounded" width={72} height={14} sx={{ mt: 0.5, borderRadius: "4px" }} />
    </Stack>
  );
}

function StatusDot({ color }) {
  return (
    <Box
      sx={{
        width: 16,
        height: 16,
        borderRadius: "50px",
        border: `1px dashed ${color}`,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          bgcolor: color,
          flexShrink: 0,
        }}
      />
    </Box>
  );
}

export function StatusSelect({ value, onChange, placeholder }) {
  const statusItems = Object.values(STATUS_META);

  return (
    <FormControl size="small" sx={{ width: 248 }}>
      <Select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        displayEmpty
        IconComponent={DropdownArrow}
        renderValue={(selectedValue) => {
          const selectedStatus = STATUS_META[getStatusKey(selectedValue)];
          if (!selectedStatus) {
            return (
              <Typography sx={tv2SelectPlaceholderTypographySx}>
                {placeholder}
              </Typography>
            );
          }
          return (
            <Box sx={tv2SelectValueRowSx}>
              <StatusDot color={selectedStatus.color} />
              <Typography sx={tv2SelectValueTypographySx}>
                {selectedStatus.label}
              </Typography>
            </Box>
          );
        }}
        MenuProps={selectMenuProps}
        sx={tv2PropertySelectSx}
      >
        {statusItems.map((item) => (
          <MenuItem key={item.label} value={item.label} disableRipple>
            <Stack direction="row" alignItems="center" spacing={1}>
              <StatusDot color={item.color} />
              <Typography sx={{ fontSize: 14, color: "#405261", fontFamily: inter }}>
                {item.menuLabel}
              </Typography>
            </Stack>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export function PrioritySelect({ value, onChange, placeholder }) {
  return (
    <FormControl size="small" sx={{ width: 248 }}>
      <Select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        displayEmpty
        IconComponent={DropdownArrow}
        renderValue={(selectedValue) => {
          const selectedPriority = PRIORITY_META.find((p) => p.key === selectedValue);
          if (!selectedPriority) {
            return (
              <Typography sx={tv2SelectPlaceholderTypographySx}>
                {placeholder}
              </Typography>
            );
          }
          const SelectedIcon = selectedPriority.icon;
          return (
            <Box sx={{ ...tv2SelectValueRowSx, gap: selectedPriority.gap }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  lineHeight: 0,
                }}
              >
                <SelectedIcon style={selectedPriority.iconSx} />
              </Box>
              <Typography sx={tv2SelectValueTypographySx}>
                {selectedPriority.key}
              </Typography>
            </Box>
          );
        }}
        MenuProps={selectMenuProps}
        sx={tv2PropertySelectSx}
      >
        {PRIORITY_META.map((item) => {
          const ItemIcon = item.icon;
          return (
            <MenuItem key={item.key} value={item.key} disableRipple>
              <Stack direction="row" alignItems="center" spacing={item.gap}>
                <ItemIcon style={item.iconSx} />
                <Typography sx={{ fontSize: 14, color: "#405261", fontFamily: inter }}>{item.key}</Typography>
              </Stack>
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
}

export function Field({ label, children }) {
  return (
    <Stack gap={0.75}>
      <Typography sx={sxMetaLabel}>{label}</Typography>
      {children}
    </Stack>
  );
}

export function SourceCreatedLastBlock({ ticket }) {
  return (
    <Stack gap={1.75}>
      <Stack gap={0.5}>
        <Typography sx={sxMetaLabel}>Source</Typography>
        <Stack direction="row" alignItems="center" gap={1}>
          <Box sx={{ width: 16, height: 16, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <IconSource16 />
          </Box>
          <Typography sx={{ ...sxMetaDateValue, fontWeight: 400 }}>{ticket.source}</Typography>
        </Stack>
      </Stack>
      <Stack gap={0.5}>
        <Typography sx={sxMetaLabel}>Created</Typography>
        <Typography sx={sxMetaDateValue}>{ticket.created}</Typography>
      </Stack>
      <Stack gap={0.5}>
        <Typography sx={sxMetaLabel}>Last message</Typography>
        <Typography sx={sxMetaDateValue}>{ticket.lastMessage}</Typography>
      </Stack>
    </Stack>
  );
}
